import React from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/shared/components/ui/button";
import { Download } from "lucide-react";
import FullscreenLayout from "@/pages/layouts/FullScreenLayout";

const PrivacyPolicy: React.FC = () => {
  const downloadPrivacyPolicy = () => {
    // Import jsPDF dynamically
    import("jspdf")
      .then(({ default: jsPDF }) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.width;
        const pageHeight = doc.internal.pageSize.height;
        const margin = 20;
        let yPosition = margin;
        const lineHeight = 6;

        // Helper function to add text with word wrapping
        const addText = (text: string, fontSize: number = 10, isBold: boolean = false) => {
          doc.setFontSize(fontSize);
          if (isBold) {
            doc.setFont("helvetica", "bold");
          } else {
            doc.setFont("helvetica", "normal");
          }

          const lines = doc.splitTextToSize(text, pageWidth - 2 * margin);

          for (const line of lines) {
            if (yPosition + lineHeight > pageHeight - margin) {
              doc.addPage();
              yPosition = margin;
            }
            doc.text(line, margin, yPosition);
            yPosition += lineHeight;
          }
          yPosition += 2; // Add some space after text
        };

        // Helper function to add a new page if needed
        const checkNewPage = () => {
          if (yPosition + 20 > pageHeight - margin) {
            doc.addPage();
            yPosition = margin;
          }
        };

        // Title
        addText("Elayne Privacy Policy", 16, true);
        addText("Effective Date: January 1, 2025", 12, true);
        yPosition += 10;

        // Introduction
        addText("Introduction", 14, true);
        addText(
          'This Elayne Privacy Policy ("Privacy Policy") describes how Ildiko Inc., dba Elayne (as defined in "Who We Are" below) collects, uses, and shares information obtained through our websites, mobile applications, and any other online interactions you have with us (collectively, the "Services").',
        );
        addText(
          "In some circumstances, we may provide you with additional or supplemental privacy notices that apply because of the specific products or services you obtain from us, or due to different laws that may apply to you. Some states grant additional privacy rights—see the Additional Information Regarding Your State Privacy Rights section to learn more.",
        );
        addText(
          "By using any of our Services, you acknowledge the privacy practices described in this Privacy Policy and confirm that you have read this policy and any other policies referenced herein.",
        );

        // Section 1: Who We Are
        addText("1. Who We Are", 14, true);
        addText(
          'Ildiko Inc., doing business as Elayne ("Elayne," "we," "us," or "our"), provides an estate-planning and post-loss support platform through our websites (https://elayne.com, https://app.elayne.com) and the Elayne | Family Estates mobile application (collectively, the "Services"). This Privacy Policy explains how we collect, use, disclose, and protect personal information when you interact with our Services in the United States.',
        );
        addText(
          "We do not knowingly provide the Services to anyone under 16 years of age, and the Services are intended for U.S. residents only.",
        );

        // Legal Disclaimer
        checkNewPage();
        addText("IMPORTANT LEGAL DISCLAIMER", 12, true);
        addText(
          "ELAYNE IS NOT A LAW FIRM OR FINANCIAL SERVICES COMPANY AND MAY NOT PERFORM SERVICES PERFORMED BY AN ATTORNEY OR FINANCIAL PROFESSIONAL. AT NO TIME DOES THE SOFTWARE OR ELAYNE REVIEW ANY DOCUMENTS THAT ARE CREATED THROUGH THE SERVICES FOR LEGAL SUFFICIENCY, DRAW LEGAL CONCLUSIONS, PROVIDE LEGAL ADVICE, OPINIONS OR RECOMMENDATIONS ABOUT YOUR LEGAL RIGHTS, REMEDIES, DEFENSES, OPTIONS, SELECTION OF FORMS OR STRATEGIES, OR APPLY THE LAW TO THE FACTS OF YOUR PARTICULAR SITUATION, OR ASSESS YOUR FINANCIAL OR TAX LIABILITIES. ELAYNE, ITS SERVICES, AND ITS FORMS OR TEMPLATES ARE NOT A SUBSTITUTE FOR THE ADVICE OR SERVICES OF AN ATTORNEY OR FINANCIAL PROFESSIONAL. THE LEGAL OR FINANCIAL INFORMATION CONTAINED HEREIN IS NOT LEGAL OR FINANCIAL ADVICE AND IS NOT GUARANTEED TO BE CORRECT, COMPLETE OR UP TO DATE. YOU ACKNOWLEDGE AND AGREE THAT ANY DOCUMENTS AND INFORMATION AVAILABLE THROUGH THE SERVICES ARE INTENDED TO BE REVIEWED BY AND DISCUSSED WITH AN ATTORNEY OR FINANCIAL PROFESSIONAL.",
        );

        // Section 2: Information We Collect
        addText("2. Information We Collect", 14, true);
        addText("We collect the following categories of information:");
        addText("• Personal Identifiers: Name, email address, postal address, phone number");
        addText("• Government IDs: Social Security number, driver's license, or passport number");
        addText(
          "• Financial Information: Bank-account and routing numbers, account balances, assets, income, credit-report data",
        );
        addText("• Demographic Information: Race/ethnicity, gender, age, veteran status");
        addText(
          "• Device & Usage Data: IP address, browser type, device identifiers, cookies, app usage metrics",
        );
        addText("• User-Generated Content: Documents, photos, chat messages, notes you upload");
        addText(
          "• Loved Ones Data: Personal information relating to a deceased loved one or an individual for whom estate planning services are being provided",
        );
        addText(
          "• Sensitive Data: Certain financial data, government IDs, and demographics listed above",
        );
        addText("We do not collect precise geolocation data.");

        // Section 3: Sources of Information
        addText("3. Sources of Information", 14, true);
        addText(
          "• Directly from you - when you create an account, complete forms, upload documents, or communicate with us.",
        );
        addText(
          "• Automatically - via cookies, SDKs, and similar technologies that operate on our websites and apps.",
        );
        addText(
          "• Third-party integrations - such as Plaid (to link financial accounts) and U.S. credit bureaus.",
        );
        addText("• Lead-generation partners - e.g., Amplemarket.");
        addText(
          "• Records search services and public databases - We use the data provided to us about deceased individuals to collect additional information through records search services and public database searches, and to submit the same to third parties related to the deceased individual to provide our Services.",
        );
        addText(
          "• Publicly available sources - We may collect information from publicly accessible sources, including but not limited to government records, court filings, published directories, social media profiles, news articles, and other lawfully accessible online resources, to the extent permitted by applicable law and consistent with the legitimate interests of providing our Services.",
        );

        // Section 4: How We Use Information
        addText("4. How We Use Information", 14, true);
        addText("We use personal information to:");
        addText(
          "1. Provide, maintain, and improve the Services, including account creation and document processing;",
        );
        addText("2. Personalize dashboards, task checklists, and recommendations;");
        addText("3. Send transactional emails and in-app or SMS notifications;");
        addText("4. Conduct analytics and product-improvement research;");
        addText("5. Detect, investigate, and prevent security incidents or fraud;");
        addText("6. Comply with legal and regulatory obligations; and");
        addText("7. Enforce our Terms of Service and other policies.");

        // Section 5: How We Share Information
        addText("5. How We Share Information", 14, true);
        addText("We disclose personal information only as described below:");
        addText(
          "• Service Providers (hosting, payment, analytics, CRM) - Operate the Services on our behalf",
        );
        addText("• Integration Partners (e.g., Plaid) - Facilitate features you choose to enable");
        addText(
          "• Legal / Compliance - Respond to court orders, government requests, or to exercise legal rights",
        );
        addText("• Corporate Transactions - If we undertake a merger, acquisition, or asset sale");
        addText(
          "We do not sell or share personal information for cross-context behavioral advertising, and we have no internal affiliates with whom we share data for their own marketing purposes.",
        );

        // Section 6: Your Choices & Rights
        addText("6. Your Choices & Rights", 14, true);
        addText(
          "• Email & In-App Communications - You may opt out at any time via the unsubscribe link in emails or the notification settings in your account.",
        );
        addText(
          "• State Privacy Rights - We honor all rights provided under U.S. state privacy laws, including the rights to access, correct, delete, and obtain a copy of your personal information. Requests may be submitted in-app or by emailing support@elayne.com.",
        );
        addText(
          "• Deletion - Upon verified request, we will delete your personal information unless we are legally required or permitted to retain it.",
        );

        // Section 7: Data Retention
        addText("7. Data Retention", 14, true);
        addText(
          "We retain personal information for as long as we deem it as reasonably necessary to maintain and expand our relationship and provide you with our Services and offerings; to comply with our contractual obligations; or to protect ourselves from any potential disputes (i.e., as required by laws applicable to log-keeping, records and bookkeeping, and in order to have proof and evidence concerning our relationship, should any legal issues arise following your discontinuance of use), all in accordance with our data retention policy. If your access to the Services is terminated, we will delete personal information you uploaded to the Services (such as documents, photos, and user-generated content), in accordance with our Terms of Service, though we may retain certain information as required by law.",
        );

        // Section 8: Security
        addText("8. Security", 14, true);
        addText(
          "We employ industry-standard safeguards, including encryption at rest and in transit, network segmentation, least-privilege access controls, and annual SOC 2 Type II audits.",
        );

        // Section 9: External Links
        addText("9. External Links", 14, true);
        addText(
          "While our Services may contain links or integrations to other websites, social networks or services, we are not responsible for their privacy practices, and encourage you to pay attention when you leave our Services for the website or services of such third parties, or interact with social network integrations. We recommend you read the privacy policies of each and every website and service you visit. This Privacy Policy only applies to our Services.",
        );

        // Section 10: Changes to This Policy
        addText("10. Changes to This Policy", 14, true);
        addText(
          'We may update this Privacy Policy from time to time. When we do, we will revise the "Effective Date" above and, if the changes are material, provide additional notice as required by law. Continued use of the Services after the Effective Date constitutes acceptance of the revised policy.',
        );

        // Section 11: Contact Us
        addText("11. Contact Us", 14, true);
        addText(
          "If you have any questions about this Privacy Policy or our privacy practices, please contact us at:",
        );
        addText("Elayne Privacy Team");
        addText("8 The Green, STE R");
        addText("Dover, DE 19901");
        addText("Email: support@elayne.com");

        // Save the PDF
        doc.save("Elayne-Privacy-Policy.pdf");
      })
      .catch((error) => {
        console.error("Error loading jsPDF:", error);
        // Fallback to HTML download if jsPDF fails to load
        alert("Error loading PDF generator. Please try again or contact support.");
      });
  };

  return (
    <FullscreenLayout className="overflow-y-auto" childrenContainerClassName="px-4 sm:px-0 lg:px-0">
      <Helmet>
        <title>Privacy Policy - Elayne</title>
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 md:py-8">
        <div className="flex items-center justify-end mb-6">
          <Button onClick={downloadPrivacyPolicy} variant="outline">
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>

        <div>
          <h1 className="border-b-2 border-primary pb-4 mb-6">Elayne Privacy Policy</h1>

          <p className="font-medium mb-6">Effective Date: January 1, 2025</p>

          <section className="mb-8">
            <h4 className="mb-4">Introduction</h4>
            <p className="mb-4">
              This Elayne Privacy Policy ("Privacy Policy") describes how Ildiko Inc., dba Elayne
              (as defined in "Who We Are" below) collects, uses, and shares information obtained
              through our websites, mobile applications, and any other online interactions you have
              with us (collectively, the "Services").
            </p>

            <p className="mb-4">
              In some circumstances, we may provide you with additional or supplemental privacy
              notices that apply because of the specific products or services you obtain from us, or
              due to different laws that may apply to you. Some states grant additional privacy
              rights—see the{" "}
              <strong>Additional Information Regarding Your State Privacy Rights</strong> section to
              learn more.
            </p>

            <p className="mb-4">
              By using any of our Services, you acknowledge the privacy practices described in this
              Privacy Policy and confirm that you have read this policy and any other policies
              referenced herein.
            </p>
          </section>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">1. Who We Are</h3>
          <p className="mb-4">
            Ildiko Inc., doing business as <strong>Elayne</strong> ("Elayne," "we," "us," or "our"),
            provides an estate-planning and post-loss support platform through our websites (
            <a href="https://elayne.com" className="text-primary underline hover:text-secondary">
              https://elayne.com
            </a>
            ,{" "}
            <a
              href="https://app.elayne.com"
              className="text-primary underline hover:text-secondary"
            >
              https://app.elayne.com
            </a>
            ) and the <em>Elayne | Family Estates</em> mobile application (collectively, the
            "Services"). This Privacy Policy explains how we collect, use, disclose, and protect
            personal information when you interact with our Services in the United States.
          </p>

          <p className="mb-4">
            We do <strong>not</strong> knowingly provide the Services to anyone under 16 years of
            age, and the Services are intended for U.S. residents only.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 my-6 font-semibold">
            ELAYNE IS NOT A LAW FIRM OR FINANCIAL SERVICES COMPANY AND MAY NOT PERFORM SERVICES
            PERFORMED BY AN ATTORNEY OR FINANCIAL PROFESSIONAL. AT NO TIME DOES THE SOFTWARE OR
            ELAYNE REVIEW ANY DOCUMENTS THAT ARE CREATED THROUGH THE SERVICES FOR LEGAL SUFFICIENCY,
            DRAW LEGAL CONCLUSIONS, PROVIDE LEGAL ADVICE, OPINIONS OR RECOMMENDATIONS ABOUT YOUR
            LEGAL RIGHTS, REMEDIES, DEFENSES, OPTIONS, SELECTION OF FORMS OR STRATEGIES, OR APPLY
            THE LAW TO THE FACTS OF YOUR PARTICULAR SITUATION, OR ASSESS YOUR FINANCIAL OR TAX
            LIABILITIES. ELAYNE, ITS SERVICES, AND ITS FORMS OR TEMPLATES ARE NOT A SUBSTITUTE FOR
            THE ADVICE OR SERVICES OF AN ATTORNEY OR FINANCIAL PROFESSIONAL. THE LEGAL OR FINANCIAL
            INFORMATION CONTAINED HEREIN IS NOT LEGAL OR FINANCIAL ADVICE AND IS NOT GUARANTEED TO
            BE CORRECT, COMPLETE OR UP TO DATE. YOU ACKNOWLEDGE AND AGREE THAT ANY DOCUMENTS AND
            INFORMATION AVAILABLE THROUGH THE SERVICES ARE INTENDED TO BE REVIEWED BY AND DISCUSSED
            WITH AN ATTORNEY OR FINANCIAL PROFESSIONAL.
          </div>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">2. Information We Collect</h3>
          <p className="mb-4">We collect the following categories of information:</p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border border-border rounded-lg">
              <thead>
                <tr className="bg-muted">
                  <th className="border border-border p-3 text-left font-semibold">Category</th>
                  <th className="border border-border p-3 text-left font-semibold">Examples</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Personal Identifiers</strong>
                  </td>
                  <td className="border border-border p-3">
                    Name, email address, postal address, phone number
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Government IDs</strong>
                  </td>
                  <td className="border border-border p-3">
                    Social Security number, driver's license, or passport number
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Financial Information</strong>
                  </td>
                  <td className="border border-border p-3">
                    Bank-account and routing numbers, account balances, assets, income,
                    credit-report data
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Demographic Information</strong>
                  </td>
                  <td className="border border-border p-3">
                    Race/ethnicity, gender, age, veteran status
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Device & Usage Data</strong>
                  </td>
                  <td className="border border-border p-3">
                    IP address, browser type, device identifiers, cookies, app usage metrics
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>User-Generated Content</strong>
                  </td>
                  <td className="border border-border p-3">
                    Documents, photos, chat messages, notes you upload
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Loved Ones Data</strong>
                  </td>
                  <td className="border border-border p-3">
                    Personal information relating to a deceased loved one or an individual for whom
                    estate planning services are being provided
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Sensitive Data (as defined under applicable state law)</strong>
                  </td>
                  <td className="border border-border p-3">
                    Certain financial data, government IDs, and demographics listed above
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mb-6">
            We do <strong>not</strong> collect precise geolocation data.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">3. Sources of Information</h3>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>
              <strong>Directly from you</strong> - when you create an account, complete forms,
              upload documents, or communicate with us.
            </li>
            <li>
              <strong>Automatically</strong> - via cookies, SDKs, and similar technologies that
              operate on our websites and apps.
            </li>
            <li>
              <strong>Third-party integrations</strong> - such as Plaid (to link financial accounts)
              and U.S. credit bureaus.
            </li>
            <li>
              <strong>Lead-generation partners</strong> - e.g., Amplemarket.
            </li>
            <li>
              <strong>Records search services and public databases</strong> - We use the data
              provided to us about deceased individuals to collect additional information through
              records search services and public database searches, and to submit the same to third
              parties related to the deceased individual to provide our Services.
            </li>
            <li>
              <strong>Publicly available sources</strong> - We may collect information from publicly
              accessible sources, including but not limited to government records, court filings,
              published directories, social media profiles, news articles, and other lawfully
              accessible online resources, to the extent permitted by applicable law and consistent
              with the legitimate interests of providing our Services.
            </li>
          </ul>

          <h3 className="border-b border-primary pb-2 mb-4 mt-4">4. How We Use Information</h3>
          <p className="mb-4">We use personal information to:</p>
          <ol className="list-decimal pl-6 mb-6 space-y-2">
            <li>
              Provide, maintain, and improve the Services, including account creation and document
              processing;
            </li>
            <li>Personalize dashboards, task checklists, and recommendations;</li>
            <li>Send transactional emails and in-app or SMS notifications;</li>
            <li>Conduct analytics and product-improvement research;</li>
            <li>Detect, investigate, and prevent security incidents or fraud;</li>
            <li>Comply with legal and regulatory obligations; and</li>
            <li>Enforce our Terms of Service and other policies.</li>
          </ol>

          <h4 className="mb-4">Sharing With Affiliates</h4>
          <p className="mb-4">
            Elayne also shares your information with our Affiliates. For example, we share, or may
            share, your information:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>
              With Affiliates that act as vendors, service providers and consultants that access
              personal information to perform services for us, such as companies that assist us with
              marketing services
            </li>
            <li>With Affiliates for their own services and marketing purposes</li>
            <li>
              With Affiliates for regulatory reporting purposes and capturing performance metrics
            </li>
            <li>
              With Affiliates if we believe (1) that your actions are inconsistent with our user
              agreements or policies, (2) that you have violated the law, or (3) it's necessary to
              protect the rights, property and safety of Elayne, our users, the public or others
            </li>
            <li>
              In connection with, or during negotiations concerning, any merger, sale of company
              assets, financing or acquisition of all or a portion of our business by another
              company
            </li>
            <li>With Affiliates with your consent or at your direction</li>
          </ul>

          <p className="mb-4">
            We also share aggregated or de-identified information that cannot reasonably be used to
            identify you.
          </p>

          <p className="mb-6">
            We <strong>do not</strong> use personal information for off-site targeted advertising.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">5. How We Share Information</h3>
          <p className="mb-4">We disclose personal information only as described below:</p>

          <div className="overflow-x-auto mb-6">
            <table className="w-full border border-border rounded-lg">
              <thead>
                <tr className="bg-muted">
                  <th className="border border-border p-3 text-left font-semibold">Recipient</th>
                  <th className="border border-border p-3 text-left font-semibold">Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Service Providers</strong> (hosting, payment, analytics, CRM)
                  </td>
                  <td className="border border-border p-3">Operate the Services on our behalf</td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Integration Partners</strong> (e.g., Plaid)
                  </td>
                  <td className="border border-border p-3">
                    Facilitate features you choose to enable
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Legal / Compliance</strong>
                  </td>
                  <td className="border border-border p-3">
                    Respond to court orders, government requests, or to exercise legal rights
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-3">
                    <strong>Corporate Transactions</strong>
                  </td>
                  <td className="border border-border p-3">
                    If we undertake a merger, acquisition, or asset sale
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mb-6">
            We <strong>do not</strong> sell or share personal information for cross-context
            behavioral advertising, and we have no internal affiliates with whom we share data for
            their own marketing purposes.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">6. Your Choices & Rights</h3>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>
              <strong>Email & In-App Communications</strong> - You may opt out at any time via the
              unsubscribe link in emails or the notification settings in your account.
            </li>
            <li>
              <strong>State Privacy Rights</strong> - We honor all rights provided under U.S. state
              privacy laws, including the rights to access, correct, delete, and obtain a copy of
              your personal information. Requests may be submitted in-app or by emailing{" "}
              <a
                href="mailto:support@elayne.com"
                className="text-primary underline hover:text-secondary"
              >
                support@elayne.com
              </a>
              .
            </li>
            <li>
              <strong>Deletion</strong> - Upon verified request, we will delete your personal
              information unless we are legally required or permitted to retain it.
            </li>
          </ul>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">7. Data Retention</h3>
          <p className="mb-4">
            We retain personal information for as long as we deem it as reasonably necessary to
            maintain and expand our relationship and provide you with our Services and offerings; to
            comply with our contractual obligations; or to protect ourselves from any potential
            disputes (i.e., as required by laws applicable to log-keeping, records and bookkeeping,
            and in order to have proof and evidence concerning our relationship, should any legal
            issues arise following your discontinuance of use), all in accordance with our data
            retention policy. If your access to the Services is terminated, we will delete personal
            information you uploaded to the Services (such as documents, photos, and user-generated
            content), in accordance with our Terms of Service, though we may retain certain
            information as required by law.
          </p>

          <p className="mb-4">
            To determine the appropriate period for personal information, we consider the amount,
            nature, and sensitivity of the personal information, the potential risk of harm from
            unauthorized use or disclosure of your personal information, the purposes for which we
            process your personal information and the applicable legal requirements. When personal
            information is no longer required, we will securely delete or anonymize it in accordance
            with our data retention policy and applicable laws.
          </p>

          <p className="mb-6">
            If you have any questions regarding our data retention practices, please contact us by
            email at{" "}
            <a
              href="mailto:support@elayne.com"
              className="text-primary underline hover:text-secondary"
            >
              support@elayne.com
            </a>
            .
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">8. Security</h3>
          <p className="mb-6">
            We employ industry-standard safeguards, including encryption at rest and in transit,
            network segmentation, least-privilege access controls, and annual SOC 2 Type II audits.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">9. External Links</h3>
          <p className="mb-6">
            While our Services may contain links or integrations to other websites, social networks
            or services, we are not responsible for their privacy practices, and encourage you to
            pay attention when you leave our Services for the website or services of such third
            parties, or interact with social network integrations. We recommend you read the privacy
            policies of each and every website and service you visit. This Privacy Policy only
            applies to our Services.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">10. Changes to This Policy</h3>
          <p className="mb-6">
            We may update this Privacy Policy from time to time. When we do, we will revise the
            "Effective Date" above and, if the changes are material, provide additional notice as
            required by law. Continued use of the Services after the Effective Date constitutes
            acceptance of the revised policy.
          </p>

          <h3 className="mt-8 border-b border-primary pb-2 mb-4">11. Contact Us</h3>
          <p className="mb-4">
            If you have any questions about this Privacy Policy or our privacy practices, please
            contact us at:
          </p>

          <div className="mt-4">
            <p>
              <span className="font-semibold">Elayne Privacy Team</span>
              <br />
              8 The Green, STE R<br />
              Dover, DE 19901
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@elayne.com"
                className="text-primary underline hover:text-secondary"
              >
                support@elayne.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </FullscreenLayout>
  );
};

export default PrivacyPolicy;
